/**
 * section-sw-pdp-kit-builder.js — V2/FORGE kit builder.
 *
 * Pure DOM logic, no GSAP (subtotal math is not an animation concern):
 * - Kit-type radios broadcast `sw:kit-option`; product-info.js resolves the
 *   matching variant and answers with `sw:variant-change`.
 * - Live itemized subtotal (P-08): base = current variant price, plus every
 *   checked addon checkbox rendered by the addon-upsell section. Addon inputs
 *   live in a SIBLING section, so they are read at document level and changes
 *   are picked up via a single document-level `change` delegation.
 * - Mirrors the subtotal into the sticky ATC bar ([data-atc-bar-price]), and
 *   with settings.full_column_summary on, lends that bar the Kit / Design
 *   receipt so the running config survives the whole right-column scroll.
 * - The summary CTA submits product-info's buy form (multi-item addon logic
 *   lives there); it stays hidden until JS runs.
 *
 * Document-level listeners are registered exactly once at module scope and
 * fan out to per-section state kept on the root element — re-initialising in
 * the theme editor never stacks duplicate listeners.
 */
(function () {
  'use strict';
  if (window.SWPdpKitBuilder) return;
  window.SWPdpKitBuilder = true;

  function formatter(currency) {
    try {
      var intl = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      });
      return function (cents) {
        return intl.format(cents / 100);
      };
    } catch (error) {
      return function (cents) {
        return (cents / 100).toFixed(2) + ' ' + (currency || '');
      };
    }
  }

  function addonInputs() {
    return document.querySelectorAll('[data-sw-addon]');
  }

  function readyRoots() {
    return document.querySelectorAll('[data-sw-kit-builder][data-sw-kit-ready]');
  }

  function stateOf(root) {
    return root.swKitState;
  }

  function init(root) {
    if (root.hasAttribute('data-sw-kit-ready')) return;
    root.setAttribute('data-sw-kit-ready', '');

    var money = formatter(root.getAttribute('data-currency'));
    var state = {
      basePrice: parseInt(root.getAttribute('data-base-price'), 10) || 0,
      money: money
    };
    root.swKitState = state;

    var linesHost = root.querySelector('[data-sw-addon-lines]');
    var subtotalEl = root.querySelector('[data-summary-subtotal]');
    var kitEl = root.querySelector('[data-summary-kit]');
    var finishEl = root.querySelector('[data-summary-finish]');
    var summaryEl = root.querySelector('[data-order-summary]');
    var hasRecalced = false;

    /* "Configurator energy" (V2/Grip register elevation, 2026-07-13): a
       plain CSS pulse — NOT GSAP, no ScrollTrigger involved — retriggered on
       every live subtotal change so the order summary visibly reacts to a
       selection. `prefers-reduced-motion` is handled entirely in CSS
       (section-sw-pdp-kit-builder.css gates the keyframes), so this class
       toggle is inert/no-op there. Skipped on the very first render (initial
       mount isn't a "change"). */
    function pulseSummary() {
      if (!summaryEl || !hasRecalced) return;
      summaryEl.classList.remove('is-updated');
      // eslint-disable-next-line no-unused-expressions
      summaryEl.offsetWidth; // force reflow so the animation can retrigger
      summaryEl.classList.add('is-updated');
    }

    /* One hidden receipt line per addon input found in the document (the
       addon-upsell section renders the inputs; this section renders the
       receipt). Rebuilt whenever a section reload changes the input set. */
    state.buildAddonLines = function () {
      if (!linesHost) return;
      linesHost.textContent = '';
      addonInputs().forEach(function (input) {
        var line = document.createElement('div');
        line.className = 'sw-summary-line';
        line.setAttribute('data-summary-addon', input.getAttribute('data-sw-addon') || '');
        line.hidden = true;
        var name = document.createElement('span');
        name.textContent = input.getAttribute('data-addon-title') || '';
        var price = document.createElement('span');
        price.setAttribute('data-summary-addon-price', '');
        line.appendChild(name);
        line.appendChild(price);
        linesHost.appendChild(line);
      });
    };

    /* Full-column mini-summary (settings.full_column_summary, VB stage 2).
       See the section's Liquid comment for why this rides the fixed ATC bar
       instead of a taller sticky container: CSS sticky is bounded by its own
       containing block, and a JSON template cannot wrap sibling sections.
       Injected here rather than authored in product-info.liquid so the V1 /
       V-brace / LATCH templates keep exactly the markup they ship today; the
       bar is re-populated on shopify:section:load, so an editor reload of
       either section restores it. No-ops when the bar isn't rendered
       (catalogue mode, or the sticky bar setting switched off).
       ponytail: mirrors the two receipt fields only — if it ever needs the
       addon lines too, render the receipt in product-info.liquid instead. */
    function syncBar() {
      if (!root.hasAttribute('data-full-column-summary')) return;
      var bar = document.querySelector('[data-atc-bar]');
      if (!bar) return;
      var receipt = bar.querySelector('[data-atc-bar-receipt]');
      if (!receipt) {
        receipt = document.createElement('span');
        receipt.className = 'sw-atc-bar__receipt';
        receipt.setAttribute('data-atc-bar-receipt', '');
        var meta = bar.querySelector('.sw-atc-bar__meta');
        if (meta) {
          meta.insertAdjacentElement('afterend', receipt);
        } else {
          bar.appendChild(receipt);
        }
      }
      var parts = [];
      [kitEl, finishEl].forEach(function (el) {
        var text = el && el.textContent.trim();
        if (text) parts.push(text);
      });
      receipt.textContent = parts.join(' · ');
    }

    state.recalc = function () {
      var total = state.basePrice;
      addonInputs().forEach(function (input) {
        var key = input.getAttribute('data-sw-addon') || '';
        var line = root.querySelector('[data-summary-addon="' + key + '"]');
        var price = parseInt(input.getAttribute('data-price'), 10) || 0;
        if (input.checked) {
          total += price;
          if (line) {
            line.hidden = false;
            var priceEl = line.querySelector('[data-summary-addon-price]');
            if (priceEl) priceEl.textContent = money(price);
          }
        } else if (line) {
          line.hidden = true;
        }
      });
      if (subtotalEl) subtotalEl.textContent = money(total);
      document.querySelectorAll('[data-atc-bar-price]').forEach(function (el) {
        el.textContent = money(total);
      });
      pulseSummary();
      syncBar();
      hasRecalced = true;
    };

    state.onVariantChange = function (detail) {
      if (typeof detail.price === 'number') state.basePrice = detail.price;
      if (finishEl && detail.finishValue) finishEl.textContent = detail.finishValue;
      state.recalc();
    };

    /* Kit-type toggle -> product-info variant state */
    root.querySelectorAll('[data-sw-kit-option]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        if (kitEl) kitEl.textContent = radio.value;
        syncBar();
        var toggle = radio.closest('[data-sw-kit-toggle]');
        document.dispatchEvent(
          new CustomEvent('sw:kit-option', {
            detail: {
              name: toggle ? toggle.getAttribute('data-option-name') : '',
              value: radio.value
            }
          })
        );
      });
    });

    /* Summary CTA -> submit the buy form rendered by product-info */
    var cta = root.querySelector('[data-sw-kit-atc]');
    if (cta) {
      var form = document.querySelector('form[data-sw-product-form]');
      if (form) {
        cta.hidden = false;
        cta.addEventListener('click', function () {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        });
      }
    }

    state.buildAddonLines();
    state.recalc();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-sw-kit-builder]').forEach(init);
  }

  /* ----- module-level fan-out listeners (registered once) ----- */
  document.addEventListener('sw:variant-change', function (event) {
    var detail = event.detail || {};
    readyRoots().forEach(function (root) {
      var state = stateOf(root);
      if (state) state.onVariantChange(detail);
    });
  });

  document.addEventListener('change', function (event) {
    if (!event.target || !event.target.matches || !event.target.matches('[data-sw-addon]')) return;
    readyRoots().forEach(function (root) {
      var state = stateOf(root);
      if (state) state.recalc();
    });
  });

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    /* a reload elsewhere (e.g. addon-upsell) may have changed the input set */
    readyRoots().forEach(function (root) {
      var state = stateOf(root);
      if (state) {
        state.buildAddonLines();
        state.recalc();
      }
    });
  });
})();
